import styled from "@emotion/styled/macro";
import { Button, tokens } from "@sunrun/experience-ui-components";
import { useState } from "react";

type FooterOverflowMenuProps = {
  isCustomer: boolean;
  onShareClick: () => void;
  onUtilityBillClick?: () => void;
  showUtilityBill?: boolean;
  contractReviewIssueCount?: number;
  onItemsToReviewClick?: () => void;
};

const FooterOverflowMenu = ({
  onShareClick,
  onUtilityBillClick,
  showUtilityBill = false,
  contractReviewIssueCount,
  onItemsToReviewClick,
  isCustomer,
}: FooterOverflowMenuProps) => {
  const [open, setOpen] = useState(false);

  if (isCustomer) {
    return null;
  }

  const issueLabel =
    contractReviewIssueCount === 1
      ? "1 item to review"
      : `${contractReviewIssueCount} items to review`;

  return (
    <OverflowMenuWrapper>
      {open && (
        <>
          <OverflowMenuBackdrop onClick={() => setOpen(false)} />
          <OverflowMenu role="menu">
            <OverflowMenuItem
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onShareClick();
              }}
            >
              Share offer
            </OverflowMenuItem>
            {showUtilityBill && onUtilityBillClick && (
              <OverflowMenuItem
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onUtilityBillClick();
                }}
                data-testid="offer-review-footer-utility-bill"
              >
                Utility bill
              </OverflowMenuItem>
            )}
            {contractReviewIssueCount && contractReviewIssueCount > 0 && (
              <OverflowMenuItem
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onItemsToReviewClick!();
                }}
                data-testid="offer-review-items-to-review"
              >
                {issueLabel}
              </OverflowMenuItem>
            )}
          </OverflowMenu>
        </>
      )}
      <OverflowButton
        color="secondary"
        aria-label="More options"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        &#8942;
      </OverflowButton>
    </OverflowMenuWrapper>
  );
};

const OverflowMenuWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const OverflowButton = styled(Button)`
  border-radius: 80px;
  width: 40px;
  height: 40px;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const OverflowMenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 19;
`;

const OverflowMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 20;
  background: ${tokens.WHITE};
  border-radius: 12px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  min-width: 180px;
`;

const OverflowMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 18px 20px;
  background: none;
  border: none;
  text-align: left;
  font-size: 18px;
  font-weight: 400;
  color: ${tokens.BRAND_HEROBLUE};
  cursor: pointer;
  line-height: 1.3;

  & + & {
    border-top: 1px solid ${tokens.TINTS_CHARGE_BLUE_80};
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

export { FooterOverflowMenu };
